import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-multi-date-picker';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { commonService } from '../../services/common.service';
import DContext from '../../context/DContext';
import { banksService } from '../../services/banks.service';
import { walletTopupService } from '../../services/wallet-topup.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { formField, formInputStyle, formLabelStyle, formReadOnlyStyle, formSectionCard, formSectionTitle, formSelectStyles } from "../common/FormUI";
import { formatDateYYYYMMDD } from '../../utils/dateUtils';
import { Admin_Code } from '../../config/siteConfig';

const GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";

const CURRENCY_CODE = "INR";

// Top-up payment modes (shown in the Payment Mode dropdown)
const PAYMENT_MODE_OPTIONS = [
  { value: "bank_transfer", label: "Online Transfer" },
  { value: "cheque", label: "Cheque Deposit" },
  { value: "cash_in_bank", label: "Cash In Bank" },
  { value: "cash_in_office", label: "Cash at Headoffice" },
];

// Every top-up field in one place: label, input type and whether it is required.
// To rename a label or make a field optional, change it here only.
const TOPUP_FIELDS = {
  amount:          { label: "Deposit Amount",       type: "number", placeholder: "Enter deposit amount", required: true },
  payment_date:    { label: "Deposit Date",         type: "date",   placeholder: "Select deposit date", required: true },
  bank_name:       { label: "Bank Name",            type: "bank",   placeholder: "Select bank", required: true },
  account_number:  { label: "Account Number",       type: "account", placeholder: "Select account number", required: true },
  concern_person:  { label: "Concern Person",       type: "text",   placeholder: "Enter concern person name", required: true },
  cheque_drawn:     { label: "Cheque Draw on Bank",  type: "text",   placeholder: "Enter cheque draw on bank", required: true },
  cheque_issue_date:     { label: "Cheque Issue Date",    type: "date",   placeholder: "Select cheque issue date", required: true },
  cheque_number:   { label: "Cheque Number",        type: "text",   placeholder: "Enter cheque number", required: true },
  payment_ref:  { label: "Transaction Id",       type: "text",   placeholder: "Enter transaction id", required: true },
  proof:           { label: "Upload Proof",         type: "file",   required: true },
  remarks:         { label: "Remark",               type: "text",   placeholder: "Any note for the approver", required: false },
};

// Which fields (and in which order) are shown for each payment mode.
// Each mode is a list of sections; to add / remove / reorder a field edit these lists.
const PAYMENT_MODE_SECTIONS = {
  bank_transfer: [
    { title: "Deposit Detail", icon: "bi-cash-coin", fields: ["amount", "payment_date", "bank_name", "account_number"] },
    { title: "Transaction Detail", icon: "bi-receipt", fields: ["payment_ref", "proof", "remarks"] },
  ],
  cash_in_office: [
    { title: "Deposit Detail", icon: "bi-cash-coin", fields: ["amount", "payment_date", "concern_person"] },
    { title: "Transaction Detail", icon: "bi-receipt", fields: ["payment_ref", "proof", "remarks"] },
  ],
  cash_in_bank: [
    { title: "Deposit Detail", icon: "bi-cash-coin", fields: ["amount", "payment_date", "bank_name", "account_number"] },
    { title: "Transaction Detail", icon: "bi-receipt", fields: ["payment_ref", "proof", "remarks"] },
  ],
  cheque: [
    { title: "Deposit Detail", icon: "bi-cash-coin", fields: ["amount", "bank_name", "account_number"] },
    { title: "Cheque Detail", icon: "bi-journal-check", fields: ["cheque_drawn", "cheque_issue_date", "cheque_number"] },
    { title: "Transaction Detail", icon: "bi-receipt", fields: ["payment_ref", "proof", "remarks"] },
  ],
};

const getModeFields = (mode) => (PAYMENT_MODE_SECTIONS[mode] || []).flatMap((s) => s.fields);

const WITHDRAWAL_MODE_OPTIONS = [
  { value: "neft", label: "NEFT" },
  { value: "imps", label: "IMPS" },
  { value: "rtgs", label: "RTGS" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

const TABS = [
  { key: "topup", label: "Wallet Topup", icon: "bi-cash-coin" },
  // { key: "withdrawal", label: "Wallet Withdrawal", icon: "bi-box-arrow-up" },
];

const initialWithdrawal = {
  request_type: "withdrawal",
  amount: "",
  bank_id: "",
  payment_mode: "",
  remarks: "",
};

const initialTopup = {
  request_type: "topup",
  payment_mode: "",
  amount: "",
  payment_date: "",
  bank_name: "",
  account_number: "",
  concern_person: "",
  cheque_drawn: "",
  cheque_issue_date: "",
  cheque_number: "",
  payment_ref: "",
  remarks: "",
};

const WalletTopup = () => {
  const { authState } = useContext(DContext);
  const { user } = authState;

  const [loading, setLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [activeTab, setActiveTab] = useState("topup");
  const [topup, setTopup] = useState(initialTopup);
  const [withdrawal, setWithdrawal] = useState(initialWithdrawal);
  const [proofFile, setProofFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    loadBankOptions();
  }, []);

  const loadBankOptions = async () => {
    try {     
      const res = await banksService.list({ page: 1, status:'verified', role_code:Admin_Code});
      const banks = res?.resData?.data || [];
      setBankList(banks);
      setBankOptions(
        banks.map((b) => ({
          value: String(b.id),
          label: `${b.bank_name} - ${b.account_number}${b.is_primary ? " (Primary)" : ""}`,
        })),
      );
    } catch (err) {
      setBankList([]);
      setBankOptions([]);
    }
  };

  // Bank Name dropdown: unique bank names
  const bankNameOptions = [...new Set(bankList.map((b) => b.bank_name))].map((name) => ({ value: name, label: name }));

  // Account Number dropdown: only accounts of the selected bank
  const accountOptions = bankList
    .filter((b) => b.bank_name === topup.bank_name)
    .map((b) => ({ value: String(b.account_number), label: String(b.account_number) }));

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const handleChange = (e) => {
    setTopup({ ...topup, [e.target.name]: e.target.value });
  }

  const handleProofChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && !(file.type.startsWith("image/") || file.type === "application/pdf")) {
      toast.error("Only image or PDF files are allowed");
      e.target.value = "";
      setProofFile(null);
      return;
    }
    setProofFile(file);
  }

  const handleWithdrawalChange = (e) => {
    setWithdrawal({ ...withdrawal, [e.target.name]: e.target.value });
  }

  const validateWithdrawal = () => {
    if (!(Number(withdrawal.amount) > 0)) return "Enter a valid amount";
    if (!withdrawal.payment_mode) return "Payment mode is required";
    if (!(Number(withdrawal.bank_id) > 0)) return "Bank is required";
    return null;
  }

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    const err = validateWithdrawal();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Submit withdrawal request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const res = await walletTopupService.create({
        request_type: "withdrawal",
        amount: Number(withdrawal.amount),
        bank_id: Number(withdrawal.bank_id),
        payment_mode: withdrawal.payment_mode,
        remarks: withdrawal.remarks.trim(),
      });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        setWithdrawal(initialWithdrawal);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handlePaymentModeChange = (option) => {
    // Changing the mode resets every other field (and the proof file)
    setTopup({ ...initialTopup, payment_mode: option ? option.value : "" });
    setProofFile(null);
    setFileInputKey((k) => k + 1);
  }

  const handleBankNameChange = (option) => {
    setTopup({ ...topup, bank_name: option ? option.value : "", account_number: "" });
  }

  const validate = () => {
    if (!topup.payment_mode) return "Payment mode is required";
    for (const key of getModeFields(topup.payment_mode)) {
      const cfg = TOPUP_FIELDS[key];
      if (!cfg.required) continue;
      if (key === "proof") {
        if (!proofFile) return `${cfg.label} is required`;
      } else if (key === "amount") {
        if (!(Number(topup.amount) > 0)) return "Enter a valid deposit amount";
      } else if (!String(topup[key] || "").trim()) {
        return `${cfg.label} is required`;
      }
    }
    return null;
  }

  // Builds the WalletTopupRequest payload (see types/wallet-request.types.ts).
  // Only the fields of the selected payment mode are added.
  const buildPayload = (proofPath) => {
    const modeFields = getModeFields(topup.payment_mode);
    const has = (key) => modeFields.includes(key);

    const payload = {
      request_type: "topup",
      amount: Number(topup.amount),
      currency_code: CURRENCY_CODE,
      payment_mode: topup.payment_mode,
      payment_ref: topup.payment_ref.trim(),
      payment_proof: proofPath,
      remarks: topup.remarks.trim(),
    };

    if (has("payment_date")) payload.payment_date = formatDateYYYYMMDD(topup.payment_date);
    if (has("concern_person")) payload.concern_person = topup.concern_person.trim();
    if (has("cheque_number")) payload.cheque_number = topup.cheque_number.trim();
    if (has("cheque_issue_date")) payload.cheque_issue_date = formatDateYYYYMMDD(topup.cheque_issue_date);
    if (has("cheque_drawn")) payload.cheque_drawn = topup.cheque_drawn.trim();
    if (has("account_number")) {
      // bank_id of the chosen bank + account number
      const bank = bankList.find((b) => b.bank_name === topup.bank_name && String(b.account_number) === topup.account_number);
      payload.bank_id = Number(bank?.id);
    }
    return payload;
  }

  const handleSubmit = async (e) => {    
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Submit wallet topup request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const uploadRes = await commonService.uploadFile(proofFile);
      const proofPath = uploadRes?.resData?.filesUploaded?.[0]?.path;
      if (!uploadRes?.success || !proofPath) {
        toast.error(uploadRes?.message || "Payment proof upload failed");
        return;
      }
      
      const res = await walletTopupService.create(buildPayload(proofPath));
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        setTopup(initialTopup);
        setProofFile(null);
        setFileInputKey((k) => k + 1);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = formLabelStyle;
  const inputStyle = formInputStyle;
  const readOnlyStyle = formReadOnlyStyle;
  const selectStyles = formSelectStyles;
  const sectionCard = formSectionCard;
  const sectionTitle = formSectionTitle;
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => formField(label, required, node, cls);

  // Renders one top-up field from the TOPUP_FIELDS config
  const renderTopupField = (key) => {
    const cfg = TOPUP_FIELDS[key];
    let input = null;

    if (cfg.type === "number") {
      input = (
        <input type="number" min="0" className="form-control" placeholder={cfg.placeholder} name={key}
          value={topup[key]} onChange={handleChange} style={inputStyle} />
      );
    } else if (cfg.type === "text") {
      input = (
        <input type="text" className="form-control" placeholder={cfg.placeholder} name={key}
          value={topup[key]} onChange={handleChange} style={inputStyle} />
      );
    } else if (cfg.type === "date") {
      input = (
        <DatePicker
          onOpenPickNewDate={false}
          value={topup[key] || null}
          onChange={(d) => setTopup({ ...topup, [key]: d ? d.format("DD-MM-YYYY") : "" })}
          format="DD-MM-YYYY"
          placeholder={cfg.placeholder}
          maxDate={key === "payment_date" ? new Date() : undefined}
          editable={false}
          inputClass="form-control"
          containerStyle={{ width: "100%" }}
          style={{ ...inputStyle, width: "100%" }}
        />
      );
    } else if (cfg.type === "bank") {
      input = (
        <Select
          options={bankNameOptions}
          value={bankNameOptions.find(o => o.value === topup.bank_name) || null}
          onChange={handleBankNameChange}
          placeholder={cfg.placeholder}
          isClearable
          noOptionsMessage={() => "No bank accounts found. Add one in Bank Details."}
          menuPortalTarget={document.body}
          styles={selectStyles}
        />
      );
    } else if (cfg.type === "account") {
      input = (
        <Select
          options={accountOptions}
          value={accountOptions.find(o => o.value === topup.account_number) || null}
          onChange={(o) => setTopup({ ...topup, account_number: o ? o.value : "" })}
          placeholder={topup.bank_name ? cfg.placeholder : "Select bank first"}
          isClearable
          isDisabled={!topup.bank_name}
          menuPortalTarget={document.body}
          styles={selectStyles}
        />
      );
    } else if (cfg.type === "file") {
      input = (
        <input key={fileInputKey} type="file" className="form-control" accept="image/*,application/pdf"
          onChange={handleProofChange} style={{ ...inputStyle, paddingTop: 8 }} />
      );
    }

    return (
      <React.Fragment key={key}>
        {field(cfg.label, cfg.required, input)}
      </React.Fragment>
    );
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>{activeTab === "topup" ? "Wallet Top-up" : "Wallet Withdrawal"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/wallet" style={{ color: "#475569" }}>Wallet</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{activeTab === "topup" ? "Wallet Top-up" : "Wallet Withdrawal"}</span>
            </nav>
          </div>
          <Link to="/wallet" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-wallet2"></i> Wallet
          </Link>
        </div>

        <ul className="nav nav-tabs mb-3" style={{ borderBottom: "1px solid #d5dbe8" }}>
          {TABS.map((t) => (
            <li className="nav-item" key={t.key}>
              <button type="button" className={"nav-link d-flex align-items-center gap-2" + (activeTab === t.key ? " active" : "")}
                onClick={() => setActiveTab(t.key)}
                style={{ fontSize: 13, fontWeight: 600 }}>
                <i className={"bi " + t.icon}></i> {t.label}
              </button>
            </li>
          ))}
        </ul>

        {activeTab === "withdrawal" && (
          <form className="v2a-form" onSubmit={handleWithdrawalSubmit}>
            <div style={sectionCard}>
              {sectionTitle("bi-box-arrow-up", "Withdrawal Detail")}
              <div className="row g-3">
                {field("Request Type", true,
                  <Select
                    options={[{ value: "withdrawal", label: "Withdrawal" }]}
                    value={{ value: "withdrawal", label: "Withdrawal" }}
                    isDisabled
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Amount", true,
                  <input type="number" min="0" className="form-control" placeholder="Enter amount" name="amount"
                    value={withdrawal.amount} onChange={handleWithdrawalChange} style={inputStyle} />
                )}
                {field("Remarks", false,
                  <input type="text" className="form-control" placeholder="Any note for the approver" name="remarks"
                    value={withdrawal.remarks} onChange={handleWithdrawalChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div style={sectionCard}>
              {sectionTitle("bi-bank", "Bank Detail")}
              <div className="row g-3">
                {field("Payment Mode", true,
                  <Select
                    options={WITHDRAWAL_MODE_OPTIONS}
                    value={WITHDRAWAL_MODE_OPTIONS.find(o => o.value === withdrawal.payment_mode) || null}
                    onChange={(o) => setWithdrawal({ ...withdrawal, payment_mode: o ? o.value : "" })}
                    placeholder="Select payment mode"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Bank", true,
                  <Select
                    options={bankOptions}
                    value={bankOptions.find(o => o.value === String(withdrawal.bank_id)) || null}
                    onChange={(o) => setWithdrawal({ ...withdrawal, bank_id: o ? o.value : "" })}
                    placeholder="Select bank"
                    isClearable
                    noOptionsMessage={() => "No bank accounts found. Add one in Bank Details."}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/wallet" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                Submit Request
              </button>
            </div>
          </form>
        )}

        {activeTab === "topup" && (
        <form className="v2a-form" onSubmit={handleSubmit}>

          {/* One box: Payment Mode first, then only the fields of the selected mode */}
          <div style={sectionCard}>
            <div className="row g-3">
              {field("Payment Mode", true,
                <Select
                  options={PAYMENT_MODE_OPTIONS}
                  value={PAYMENT_MODE_OPTIONS.find(o => o.value === topup.payment_mode) || null}
                  onChange={handlePaymentModeChange}
                  placeholder="Select payment mode"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              )}
              {getModeFields(topup.payment_mode).map((key) => renderTopupField(key))}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Link to="/wallet" className="btn px-4 d-flex align-items-center"
              style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
              Back
            </Link>
            <button type="submit" className="btn px-4 text-white fw-semibold"
              style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
              Submit Request
            </button>
          </div>

        </form>
        )}
      </section>
    </>
  );
}

export default WalletTopup;
